import * as Sentry from "@sentry/react";

const REDACTED = "[REDACTED]";
const SENSITIVE_KEY_PATTERN =
  /(authorization|cookie|token|password|secret|api[-_]?key)/i;
const BEARER_TOKEN_PATTERN = /\bBearer\s+[A-Za-z0-9\-._~+/]+=*/gi;
const JWT_PATTERN = /\b[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\b/g;

const toNumberInRange = (
  rawValue: unknown,
  fallback: number,
  min = 0,
  max = 1,
): number => {
  const parsed = Number.parseFloat(String(rawValue ?? ""));
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
};

const sanitizeString = (value: string): string =>
  value.replace(BEARER_TOKEN_PATTERN, "Bearer [REDACTED]").replace(JWT_PATTERN, REDACTED);

const sanitizeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeValue(entry));
  }
  if (value && typeof value === "object") {
    const source = value as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(source).map(([key, entry]) => [
        key,
        SENSITIVE_KEY_PATTERN.test(key) ? REDACTED : sanitizeValue(entry),
      ]),
    );
  }
  if (typeof value === "string") {
    return sanitizeString(value);
  }
  return value;
};

const sanitizeEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
  const request = event.request;
  if (request) {
    const headers = { ...(request.headers || {}) };
    Object.keys(headers).forEach((key) => {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        headers[key] = REDACTED;
      } else if (typeof headers[key] === "string") {
        headers[key] = sanitizeString(headers[key] as string);
      }
    });
    request.headers = headers;
    if ("data" in request) {
      delete request.data;
    }
  }

  if (event.breadcrumbs?.length) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => ({
      ...crumb,
      data: sanitizeValue(crumb.data) as Record<string, unknown> | undefined,
      message:
        typeof crumb.message === "string"
          ? sanitizeString(crumb.message)
          : crumb.message,
    }));
  }

  event.extra = sanitizeValue(event.extra) as Record<string, unknown> | undefined;
  return event;
};

export const initSentry = (): void => {
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const isProductionHost = hostname === "app.nobori.ai";
  if (!isProductionHost) {
    return;
  }

  const dsnRaw = String(import.meta.env.VITE_SENTRY_DSN ?? "").trim();
  const loweredDsn = dsnRaw.toLowerCase();
  const dsn =
    !dsnRaw || loweredDsn === "undefined" || loweredDsn === "null"
      ? ""
      : dsnRaw;
  const configuredEnvironment = String(
    import.meta.env.VITE_APP_ENV || import.meta.env.VITE_ENVIRONMENT || "",
  ).trim();
  const environment = configuredEnvironment || undefined;
  if (!dsn) {
    return;
  }

  const release = String(
    import.meta.env.VITE_RELEASE || import.meta.env.VITE_GIT_SHA || "",
  ).trim();
  const tracesSampleRate = toNumberInRange(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    0.1,
  );
  const replaySessionSampleRate = toNumberInRange(
    import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
    0,
  );
  const replayOnErrorSampleRate = toNumberInRange(
    import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
    0.1,
  );

  const maybeBrowserTracingIntegration = (Sentry as any)
    .browserTracingIntegration as (() => unknown) | undefined;
  const maybeReplayIntegration = (Sentry as any).replayIntegration as
    | ((options?: Record<string, unknown>) => unknown)
    | undefined;
  const integrations: unknown[] = [];
  if (maybeBrowserTracingIntegration) {
    integrations.push(maybeBrowserTracingIntegration());
  }
  if (
    maybeReplayIntegration &&
    (replaySessionSampleRate > 0 || replayOnErrorSampleRate > 0)
  ) {
    integrations.push(
      maybeReplayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    );
  }

  Sentry.init({
    dsn,
    enabled: true,
    environment,
    release: release || undefined,
    sendDefaultPii: false,
    integrations: integrations as any,
    tracesSampleRate,
    replaysSessionSampleRate: replaySessionSampleRate,
    replaysOnErrorSampleRate: replayOnErrorSampleRate,
    beforeSend: (event) => sanitizeEvent(event),
  });
};
